import { useEffect, useMemo, useState } from 'react';
import { Select, SelectOption } from '../Select';
import { CategoryData } from '../../../types/CategoryData';
import { useIntl } from 'react-intl';
import useFormValidation from '../../../utils/useFormValidation';
import { Validations } from '../../../interfaces';
import { Rule } from 'rc-field-form/lib/interface';
import { useCategoryDispatch, useCategoryState } from '../../../public/context';
import { StyledCategorySelector } from './style';
import {
  extractId,
  findSelectedCategory,
  findSelectedSubcategory,
  getSubcategories,
  getSubSubCategories,
} from '../../../utils/categoryHelpers';
import { Icon } from '../../Icon';
import { useBreakpoint } from 'utils/useBreakpoint';

interface AvailableCategoryOptions {
  main_categories: Array<{ id: number; name: string }>;
  sub_categories: Array<{ id: number; name: string; parent_id: number }>;
  sub_subcategories: Array<{ id: number; name: string; parent_id: number }>;
}

export interface categoryProps extends Validations {
  displayFlex?: boolean;
  admin?: boolean;
  rules?: Rule[];
  includeNonEmarketCategories?: boolean;
  hideTitle?: boolean;

  mainCategoryValue?: number;
  onMainCategoryChange?: (value: string) => void;
  subCategoryValue?: number;
  onSubCategoryChange?: (value: string) => void;
  subSubCategoryValue?: number;
  onSubSubCategoryChange?: (value: string) => void;
  selectIcon?: JSX.Element;

  mainCategoryLabel?: string;
  subCategoryLabel?: string;
  subSubCategoryLabel?: string;
  availableOptions?: AvailableCategoryOptions;
}

export const CategorySelector = ({
  admin,
  validations,
  rules,
  includeNonEmarketCategories = false,
  hideTitle = false,
  mainCategoryValue,
  onMainCategoryChange,
  subCategoryValue,
  onSubCategoryChange,
  subSubCategoryValue,
  onSubSubCategoryChange,
  mainCategoryLabel,
  subCategoryLabel,
  subSubCategoryLabel,
  availableOptions,
}: categoryProps) => {
  const intl = useIntl();
  const { formValidations } = useFormValidation();
  const { category } = useCategoryState();
  const dispatch = useCategoryDispatch();

  const categoriesObj: CategoryData[] = Object.values(category || []);
  const [selectedCategory, setSelectedCategory] = useState<CategoryData>();
  const [selectedSubcategory, setSelectedSubcategory] =
    useState<CategoryData>();
  const [selectedSubSubCategory, setSelectedSubSubCategory] =
    useState<CategoryData>();

  const getFilteredCategories = (categories: CategoryData[]): CategoryData[] => {
    if (!availableOptions) return categories;
    const availableIds = new Set(availableOptions.main_categories.map(c => c.id));
    return categories.filter(cat => availableIds.has(cat.id));
  };

  const isMainCategoryControlled = mainCategoryValue !== undefined;
  const isSubCategoryControlled = subCategoryValue !== undefined;
  const isSubSubCategoryControlled = subSubCategoryValue !== undefined;
  const { isLgDown } = useBreakpoint();

  const mainForSubTree = useMemo(() => {
    if (isMainCategoryControlled) {
      return findSelectedCategory(categoriesObj, mainCategoryValue!);
    }
    return selectedCategory;
  }, [
    isMainCategoryControlled,
    mainCategoryValue,
    categoriesObj,
    selectedCategory,
  ]);

  const subcategoriesList = useMemo(
    () => getSubcategories(mainForSubTree, admin || includeNonEmarketCategories),
    [mainForSubTree, admin, includeNonEmarketCategories]
  );

  const subForSubSubTree = useMemo(() => {
    if (isSubCategoryControlled) {
      return findSelectedSubcategory(subcategoriesList, subCategoryValue!);
    }
    return selectedSubcategory;
  }, [
    isSubCategoryControlled,
    subCategoryValue,
    subcategoriesList,
    selectedSubcategory,
  ]);

  const subSubCategoriesList = useMemo(
    () =>
      getSubSubCategories(subForSubSubTree, admin || includeNonEmarketCategories),
    [subForSubSubTree, admin, includeNonEmarketCategories]
  );

  const currentMainCategoryId = isMainCategoryControlled
    ? mainCategoryValue
    : selectedCategory?.id;
  const currentSubCategoryId = isSubCategoryControlled
    ? subCategoryValue
    : selectedSubcategory?.id;
  const currentSubSubCategoryId = isSubSubCategoryControlled
    ? subSubCategoryValue
    : selectedSubSubCategory?.id;

  useEffect(() => {
    if (!isMainCategoryControlled) return;
    dispatch({
      type: 'SAVE_ACTIVE_CATEGORY',
      activeCategory: mainForSubTree ?? null,
    });
  }, [isMainCategoryControlled, mainForSubTree, dispatch]);

  const handleMainCategoryChange = (raw: any) => {
    const id = String(extractId(raw));

    if (onMainCategoryChange) {
      onMainCategoryChange(id);
      return;
    }

    const selected = findSelectedCategory(categoriesObj, id);
    setSelectedCategory(selected);
    setSelectedSubcategory(undefined);
    setSelectedSubSubCategory(undefined);

    dispatch({
      type: 'SAVE_ACTIVE_CATEGORY',
      activeCategory: selected || null,
    });
  };

  const handleSubcategoryChangeLocal = (raw: any) => {
    const id = String(extractId(raw));

    if (onSubCategoryChange) {
      onSubCategoryChange(id);
      return;
    }

    const selected = findSelectedSubcategory(subcategoriesList, id);
    setSelectedSubcategory(selected);
    setSelectedSubSubCategory(undefined);
  };

  const handleSubSubCategoryChangeLocal = (raw: any) => {
    const id = String(extractId(raw));

    if (onSubSubCategoryChange) {
      onSubSubCategoryChange(id);
      return;
    }

    const selected = findSelectedSubcategory(subSubCategoriesList, id);
    setSelectedSubSubCategory(selected);
  };

  return (
    <StyledCategorySelector className={'styled-category-selector'}>
      <div className={'category-sel-wrap'}>
        <div>
          <div className={admin ? 'admin' : 'user'}>
            <div className={'category-selector-item'}>
              {!admin && !hideTitle && (
                <div className="form-title">
                  {intl.formatMessage({ id: 'form.categories_title' })}
                </div>
              )}
              <Select
                placeholder={intl.formatMessage({
                  id: 'form.select_main_category',
                })}
                className="drop-down"
                showSearch={false}
                name={'main-category'}
                label={
                  admin
                    ? mainCategoryLabel ||
                    intl.formatMessage({ id: 'general.group' })
                    : mainCategoryLabel
                }
                size={'small'}
                allowClear
                onChange={handleMainCategoryChange}
                onClear={() => handleMainCategoryChange(undefined)}
                value={currentMainCategoryId}
                rules={validations ? formValidations(validations) : rules}
                labelInValue={admin}
                suffixIcon={
                  isLgDown ? (
                    <Icon name={'arrow-mobile'} />
                  ) : (
                    <Icon name={'arrow'} />
                  )
                }
              >
                {getFilteredCategories(categoriesObj).map((category) => (
                  <SelectOption key={category.id} value={category.id}>
                    {category.name}
                  </SelectOption>
                ))}
              </Select>
              {!admin && (
                <>
                  <Select
                    placeholder={intl.formatMessage({
                      id: 'form.select_sub_category',
                    })}
                    className="drop-down"
                    showSearch={false}
                    name={'sub-category'}
                    label={subCategoryLabel}
                    size={'small'}
                    allowClear
                    onChange={handleSubcategoryChangeLocal}
                    onClear={() => handleSubcategoryChangeLocal(undefined)}
                    value={currentSubCategoryId}
                    disabled={!currentMainCategoryId}
                    rules={validations ? formValidations(validations) : rules}
                  >
                    {subcategoriesList.map((subcategory) => (
                      <SelectOption key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </SelectOption>
                    ))}
                  </Select>
                  <Select
                    placeholder={intl.formatMessage({
                      id: 'form.select_sub_subcategory',
                    })}
                    className="drop-down"
                    showSearch={false}
                    name={'sub-subcategory'}
                    label={subSubCategoryLabel}
                    size={'small'}
                    allowClear
                    onChange={handleSubSubCategoryChangeLocal}
                    onClear={() => handleSubSubCategoryChangeLocal(undefined)}
                    value={currentSubSubCategoryId}
                    disabled={!currentSubCategoryId}
                    rules={validations ? formValidations(validations) : rules}
                  >
                    {subSubCategoriesList.map((subSubCategory) => (
                      <SelectOption
                        key={subSubCategory.id}
                        value={subSubCategory.id}
                      >
                        {subSubCategory.name}
                      </SelectOption>
                    ))}
                  </Select>
                </>
              )}
            </div>

            {admin && (
              <>
                <div
                  className={'category-selector-item'}
                  style={{ display: 'flex', flexDirection: 'column' }}
                >
                  <Select
                    placeholder={intl.formatMessage({
                      id: 'form.select_sub_category',
                    })}
                    className="drop-down"
                    showSearch={false}
                    name={'sub-category'}
                    label={
                      subCategoryLabel ||
                      intl.formatMessage({ id: 'general.category' })
                    }
                    size={'small'}
                    allowClear
                    onChange={handleSubcategoryChangeLocal}
                    onClear={() => handleSubcategoryChangeLocal(undefined)}
                    value={currentSubCategoryId}
                    disabled={!currentMainCategoryId}
                    rules={validations ? formValidations(validations) : rules}
                    labelInValue
                    suffixIcon={
                      isLgDown ? (
                        <Icon name={'arrow-mobile'} />
                      ) : (
                        <Icon name={'arrow'} />
                      )
                    }
                  >
                    {
                      subcategoriesList.map((subcategory) => (
                        <SelectOption key={subcategory.id} value={subcategory.id}>
                          {subcategory.name}
                        </SelectOption>
                      ))
                    }
                  </Select >
                </div >

                <div className={'category-selector-item'}>
                  <Select
                    placeholder={intl.formatMessage({
                      id: 'form.select_sub_subcategory',
                    })}
                    className="drop-down"
                    showSearch={false}
                    name={'sub-subcategory'}
                    label={
                      subSubCategoryLabel ||
                      intl.formatMessage({ id: 'general.type' })
                    }
                    size={'small'}
                    allowClear
                    onChange={handleSubSubCategoryChangeLocal}
                    onClear={() => handleSubSubCategoryChangeLocal(undefined)}
                    value={currentSubSubCategoryId}
                    disabled={!currentSubCategoryId}
                    rules={validations ? formValidations(validations) : rules}
                    labelInValue
                    suffixIcon={
                      isLgDown ? (
                        <Icon name={'arrow-mobile'} />
                      ) : (
                        <Icon name={'arrow'} />
                      )
                    }
                  >
                    {
                      subSubCategoriesList.map((subSubCategory) => (
                        <SelectOption
                          key={subSubCategory.id}
                          value={subSubCategory.id}
                        >
                          {subSubCategory.name}
                        </SelectOption>
                      ))
                    }
                  </Select >
                </div >
              </>
            )}
          </div >
        </div >
      </div >
    </StyledCategorySelector >
  );
};
